import Cookies from 'js-cookie';
import React, { useEffect, useState } from 'react';
import Sheet from '@mui/joy/Sheet';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/joy/CssBaseline';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import LoadingIcon from '../../common/LoadingIcon';
import HeaderMainPage from './Header/HeaderMainPage';
import Visibility from '@mui/icons-material/Visibility';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { EndPoints } from '../../utils/apiEndpoints';
import { Link, useNavigate } from 'react-router-dom';
import { loginUser } from '../../services/LoginService';
import { useUserStore } from '../../store/useUserStore';
import { darkModeBackgroud } from '../../utils/constants';
import { UserLogin } from '../../domain/helpers/UserLogin';
import { FieldErrors } from '../../domain/helpers/Errors';
import { validateSchema } from '../../common/ValidateSchema';
import { useLanguageStore } from '../../store/useLanguageStore';
import { useDarkModeStore } from '../../store/useDarkModeStore';
import { TokenPayload } from '../../domain/helpers/TokenPayload';
import { loginSchema } from '../../domain/ValidationSchemas/auth/login';
import { expiredCokkieInDay, StorageItems } from '../../utils/storageItems';

const Login = () => {
    const navigate = useNavigate();
    const { setUser } = useUserStore();
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [errorLoggin, setErrorLoggin] = useState<boolean>(false);
    const [login, setLogin] = useState<UserLogin>({
        Email: '',
        Password: ''
    });
    const [errors, setErrors] = useState<FieldErrors>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [rememberMe, setRememberMe] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string>();

    useEffect(() => {
        const email = localStorage.getItem(StorageItems.EMAIL);
        const password = localStorage.getItem(StorageItems.PASSWORD);
        const updateEmail = localStorage.getItem(StorageItems.UPDATEEMAIL);

        if (email && password) {
            setLogin({ Email: email, Password: password });
            setRememberMe(true);
        } else if (updateEmail) {
            setLogin({ Email: updateEmail, Password: '' });
        }
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value, id } = e.target;
        setLogin({ ...login, [id]: value });
        delete errors?.[id];
        setErrorLoggin(false);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        localStorage.setItem(StorageItems.SELECTEDCOMPONENTS, '');

        const isValid = validateSchema(loginSchema, login);
        setErrors(isValid.errors);
        if (!isValid.result) return;
        setLoading(true);
        try {
            const response = await loginUser(login);
            if (response.resetPassword) {
                localStorage.setItem(StorageItems.UPDATEEMAIL, login.Email);
                localStorage.setItem(StorageItems.OLDPASSWORD, login.Password);
                navigate(EndPoints.RESETPASSWORD)
            }
            else if (response) {
                localStorage.removeItem(StorageItems.UPDATEEMAIL);
                localStorage.removeItem(StorageItems.OLDPASSWORD);
                Cookies.set(StorageItems.TOKEN, response.token, { expires: expiredCokkieInDay });
                Cookies.set(StorageItems.REFRESHTOKEN, response.refreshToken, { expires: expiredCokkieInDay });
                let decodedToken = jwtDecode(response.token);
                setUser(decodedToken as TokenPayload);

                if (rememberMe) {
                    localStorage.setItem(StorageItems.EMAIL, login.Email);
                    localStorage.setItem(StorageItems.PASSWORD, login.Password);
                } else {
                    localStorage.removeItem(StorageItems.EMAIL);
                    localStorage.removeItem(StorageItems.PASSWORD);
                }

                if (response.firstTimeLogin) {
                    localStorage.setItem(StorageItems.FIRSTTIMELOGIN, 'true');
                    navigate(EndPoints.ONBOARDING);
                } else {
                    localStorage.setItem(StorageItems.FIRSTTIMELOGIN, 'false');
                }
            } else {
                Cookies.remove(StorageItems.TOKEN);
                Cookies.remove(StorageItems.REFRESHTOKEN);
                setUser(null);
                setErrorLoggin(true);
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                if (error?.response?.status === 401)
                    setErrorLoggin(true);
                else setErrorLoggin(false);
                setLoginError(error?.response?.data?.errorMessage);
            }
        }
        setTimeout(() => { setLoading(false); }, 1000);
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const sheetCss = {
        py: 3,
        px: 2,
        gap: 0,
        width: 400,
        mx: 'auto',
        display: 'flex',
        borderRadius: 'md',
        flexDirection: 'column',
        boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.2)',
        background: darkMode ? darkModeBackgroud : 'white'
    };

    const fieldTextCss = {
        color: darkMode ? 'white' : 'black',
        '& .MuiInputLabel-root': {
            color: darkMode ? 'white' : 'black',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: darkMode ? 'white' : 'black',
            },
            '&:hover fieldset': {
                borderColor: darkMode ? '#90caf9' : '#1976d2',
            },
            '&.Mui-focused fieldset': {
                borderColor: darkMode ? '#90caf9' : '#1976d2',
            },
            '& input': {
                color: darkMode ? 'white' : 'black',
            },
        },
    };

    return <div className="w-100 vh-100 d-flex flex-column">
        <HeaderMainPage />
        <main className='d-flex justify-content-center align-items-center w-100 h-100'>
            <CssBaseline />
            <Sheet sx={sheetCss} >
                <div>
                    <Typography component="div" className='text-center align-middle'>
                        <LockOutlinedIcon className="text-white p-2" style={{ borderRadius: '50%', fontSize: '50px', background: 'var(--btn-theme-background)' }} />
                    </Typography>
                    <Typography
                        level="h4"
                        component="h1"
                        marginBottom={errorLoggin ? 1 : 3}
                        className='text-center fw-bold fs-2 mt-2'
                        sx={{ color: 'var(--text-theme-color)' }}
                    >
                        {language['SignIn']}
                    </Typography>
                    {
                        errorLoggin
                        && <div className='w-100 text-center'>
                            <small className="text-danger fs-6">
                                {loginError}
                            </small>
                        </div>
                    }
                </div>
                <form onSubmit={handleSubmit} className='d-flex flex-column'>
                    <TextField
                        id="Email"
                        label="Email"
                        variant="outlined"
                        className='mt-2 mb-2'
                        sx={fieldTextCss}
                        value={login.Email}
                        helperText={errors?.Email}
                        error={Boolean(errors?.Email)}
                        onChange={(e) => handleChange(e)}
                        InputLabelProps={{ style: { color: darkMode ? 'white' : 'black' } }}
                    />
                    <TextField
                        id="Password"
                        label="Password"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        className='mt-2 mb-1'
                        sx={fieldTextCss}
                        value={login.Password}
                        onChange={handleChange}
                        helperText={errors?.Password}
                        error={Boolean(errors?.Password)}
                        InputLabelProps={{ style: { color: darkMode ? 'white' : 'black' } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        aria-label="toggle password visibility"
                                        className={darkMode ? 'text-white' : ''}
                                        onClick={handleClickShowPassword}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography
                        endDecorator={
                            <Link
                                to={EndPoints.FORGOTPASSWORD}
                                style={{ color: 'var(--text-theme-color)' }}
                                onClick={() => localStorage.setItem(StorageItems.UPDATEEMAIL, login.Email)}
                            >
                                {language['Forgotpassword']}
                            </Link>}
                        fontSize="sm"
                        component="div"
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <FormGroup className={darkMode ? 'text-white' : ''}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rememberMe}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRememberMe(e.target.checked)}
                                        sx={{ color: 'var(--text-theme-color) !important' }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.875rem' }}>
                                    {language['RememberMe']}
                                </span>}
                            />
                        </FormGroup>
                    </Typography>

                    <Button className='btn-design' sx={{ mt: 4, mb: 2, fontSize: '16px' }} type="submit">
                        {
                            loading ? <LoadingIcon /> : language['SIGNIN']
                        }
                    </Button>
                </form>
            </Sheet>
        </main>
    </div >
}

export default Login;